const UTM_KEYS = ['utm_source', 'utm_medium', 'utm_campaign', 'utm_content', 'utm_term'] as const;
const STORAGE_KEY = 'hsy.signup.utm';

/**
 * AI answer engines send referral traffic with no utm_* params — a citation in
 * a ChatGPT answer is just a link. Without this, every signup sourced from the
 * GEO article programme lands in the funnel as untracked direct traffic and the
 * whole effort is unmeasurable.
 *
 * Mapping the referrer host to a synthetic utm_source means AI traffic shows up
 * in the existing `funnel_by_campaign` view with no schema or dashboard change.
 *
 * Host suffixes, matched against the referrer's hostname. Order matters only in
 * that the first match wins; keep the list small and specific rather than
 * pattern-matching, so a stray subdomain never gets misattributed.
 */
const AI_REFERRER_SOURCES: ReadonlyArray<readonly [host: string, source: string]> = [
  ['chatgpt.com', 'chatgpt'],
  ['chat.openai.com', 'chatgpt'],
  ['openai.com', 'chatgpt'],
  ['perplexity.ai', 'perplexity'],
  ['claude.ai', 'claude'],
  ['anthropic.com', 'claude'],
  ['gemini.google.com', 'gemini'],
  ['bard.google.com', 'gemini'],
  ['copilot.microsoft.com', 'copilot'],
  ['you.com', 'you'],
  ['poe.com', 'poe'],
  ['phind.com', 'phind'],
  ['grok.com', 'grok'],
  // Deliberately absent: duckduckgo.com. Its AI chat lives on a path, not a
  // distinct host, and referrer policies strip paths — so matching the host
  // would relabel every ordinary DuckDuckGo search as AI traffic and inflate
  // the exact metric this exists to measure. Under-count rather than lie.
];

/**
 * Classify a referrer URL as an AI answer engine.
 * Returns the synthetic utm_source, or undefined for anything else — including
 * ordinary search engines, which already attribute correctly as organic.
 */
export function classifyAiReferrer(referrer: string): string | undefined {
  if (!referrer) return undefined;
  let host: string;
  try {
    host = new URL(referrer).hostname.toLowerCase().replace(/^www\./, '');
  } catch {
    return undefined;
  }
  for (const [candidate, source] of AI_REFERRER_SOURCES) {
    const bare = candidate.split('/')[0];
    if (host === bare || host.endsWith(`.${bare}`)) return source;
  }
  return undefined;
}

/**
 * First-touch UTM capture for ad attribution. Ad clicks land on the marketing
 * page with utm_* params, but signup happens screens later — so we stash the
 * params on first sight and read them back at account creation. First touch
 * wins: a later organic visit never overwrites the campaign that brought them.
 */
export function captureUtmParams(
  search: string = window.location.search,
  referrer: string = typeof document !== 'undefined' ? document.referrer : '',
  pathname: string = typeof window !== 'undefined' ? window.location.pathname : '/',
): void {
  try {
    if (localStorage.getItem(STORAGE_KEY)) return;
    const params = new URLSearchParams(search);
    const utm: Record<string, string> = {};
    for (const key of UTM_KEYS) {
      const value = params.get(key);
      if (value) utm[key] = value.slice(0, 100);
    }

    // Explicit utm_source always wins. Only fall back to referrer inference
    // when the link carried no attribution of its own, so a tagged ad clicked
    // from within an AI answer is still credited to the ad.
    if (!utm.utm_source) {
      const aiSource = classifyAiReferrer(referrer);
      if (aiSource) {
        utm.utm_source = aiSource;
        utm.utm_medium = 'ai_search';
        utm.utm_campaign = utm.utm_campaign || 'ai_referral';
      }
    }

    // First-touch source context, captured even with no utm and no AI
    // referrer. A signup that reads "(organic/direct)" is undiagnosable
    // without these two facts: the referrer host (empty = typed / bookmark /
    // referrer-stripping app) and the landing page. They flow into signup
    // metadata (authStore) and landing_view props (analytics).
    if (referrer) {
      try {
        const host = new URL(referrer).hostname.toLowerCase().replace(/^www\./, '');
        if (host) utm.ref_host = host.slice(0, 100);
      } catch { /* malformed referrer — leave unset */ }
    }
    utm.landing_path = (pathname || '/').slice(0, 100);

    utm.captured_at = new Date().toISOString();
    localStorage.setItem(STORAGE_KEY, JSON.stringify(utm));
  } catch { /* storage unavailable — attribution is best-effort */ }
}

export function getCapturedUtm(): Record<string, string> | undefined {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return undefined;
    const parsed = JSON.parse(raw) as Record<string, string>;
    return Object.keys(parsed).length > 0 ? parsed : undefined;
  } catch {
    return undefined;
  }
}
